import { useEffect, useRef } from "react";

import DOMPurify from "dompurify";
import { nanoid } from "nanoid";

import { DataTableCell, DataTableRow, DataType } from "../../Models";
import DataTableBody from "./Components/DataTableBody/DataTableBody";
import DataTableFooter from "./Components/DataTableFooter/DataTableFooter";
import DataTableHeader from "./Components/DataTableHeader";

import { pasteData } from "../../context/DataTableReducer";
import useDataTableContext from "../../context/useDataTableContext";

const DataTableContainer = () => {
    const { state, dispatch } = useDataTableContext();
    const tableContainerRef = useRef<HTMLTableElement | null>(null);

    const { columns, readOnly } = state;

    // Setup event listeners for copy and paste actions
    useEffect(() => {
        if (!tableContainerRef.current) return;

        const tableContainer = tableContainerRef.current;
        tableContainer.addEventListener("copy", onCopy);
        tableContainer.addEventListener("paste", onPaste);

        return () => {
            tableContainer.removeEventListener("copy", onCopy);
            tableContainer.removeEventListener("paste", onPaste);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onCopy = (_: ClipboardEvent) => {
        // console.log("Copy event triggered.");
    };

    // TODO: Disable pasting entire tables into GID tables
    const onPaste = (event: ClipboardEvent) => {
        if (readOnly) return;

        const dataTransfer = event.clipboardData;
        if (!dataTransfer) return;

        let target: HTMLElement | null = event.target as HTMLElement;
        let entrypoint: string | undefined = undefined;

        while (target != null) {
            if (target.dataset["tableRow"]) {
                entrypoint = target.dataset["tableRow"];
                break;
            }

            target = target.parentElement;
        }

        const tableEditorData = dataTransfer.getData("application/x-ids-platform-table");
        // const plainTextData = dataTransfer.getData("text/plain");
        const htmlData = dataTransfer.getData("text/html");

        if (tableEditorData) {
            // console.log("Data copied from another ids platform table.");
            return;
        }

        const sanitizedHtml = DOMPurify.sanitize(htmlData, {
            ALLOWED_TAGS: ["table", "thead", "tbody", "tr", "th", "td", "br", "span", "div", "a"],
            ALLOWED_ATTR: ["href"],
            FORBID_TAGS: ["style", "script", "textarea", "option", "noscript", "sub", "sup"],
        });

        const template = document.createElement("template");
        template.innerHTML = sanitizedHtml.trim();

        const root = template.content.firstChild as HTMLElement;
        if (!root) return;

        if (root.nodeName === "TABLE") {
            const rows: HTMLTableRowElement[] = [];
            const pastedRows = Array.from(root.querySelectorAll("tr"));

            for (let i = 0; i < pastedRows.length; i++) {
                const row = pastedRows[i];
                if (row.cells.length === 0) continue;

                if (row.parentElement?.nodeName === "THEAD") continue;

                rows.push(row);
            }

            const content: Array<DataTableRow> = rows.map((row) => {
                let cells: Array<HTMLElement> = Array.from(row.querySelectorAll("th, td"));
                if (cells.length > columns.length) cells = cells.slice(0, columns.length);
                if (cells.length < columns.length) {
                    const diff = columns.length - cells.length;

                    for (let i = 0; i < diff; i++) {
                        cells.push(document.createElement("td"));
                    }
                }

                const insertRow: DataTableRow = {
                    id: nanoid(),
                    cells: [],
                };

                for (let i = 0; i < cells.length; i++) {
                    const cell = cells[i];
                    const column = columns[i];
                    const dataType = columns[i].dataType;

                    let value = dataType === DataType.BOOLEAN ? cell.innerText : cell.innerHTML;
                    if (dataType === DataType.BOOLEAN) {
                        value = value.toUpperCase().replaceAll("*", "");
                    }

                    const insertCell: DataTableCell = {
                        id: nanoid(),
                        value,
                        footnotes: [],
                    };

                    if (column.metadata?.softwareId) {
                        insertCell.id = `${insertRow.id}_software_${column.metadata.softwareId}`;
                        insertCell.metadata = { ...insertCell.metadata, softwareId: column.metadata.softwareId };
                    }

                    insertRow.cells.push(insertCell);
                }

                return insertRow;
            });

            dispatch(pasteData(content, entrypoint));
        } else {
            const selection = window.getSelection();
            if (!selection?.rangeCount) return;

            selection.deleteFromDocument();
            selection.getRangeAt(0).insertNode(template.content);
            selection.collapseToEnd();
        }

        event.preventDefault();
    };

    return (
        <table
            ref={tableContainerRef}
            className="group data-table mt-5 min-w-full divide-y divide-gray-300 rounded-lg border-separate border-spacing-0 text-[15px]/[20px]"
            // sx={{
            //     "@media print": {
            //         maxHeight: "unset",
            //         overflow: "visible",
            //         width: "100%",
            //     },
            // }}
        >
            <DataTableHeader />
            <DataTableBody />

            {!readOnly && <DataTableFooter />}
        </table>
    );
};

export default DataTableContainer;
